import React, { useEffect, useRef, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";
import { useRouter } from "next/router";
import { AlertCircle, Clock3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@src/lib/ui/Button";
import {
  GraphqlOperations,
  GuestFeedbackCsvUploadStatus,
  GuestFeedbackUploadBatchStatus,
} from "@src/types.generated";
import { useToast } from "@src/lib/hooks/useToast";
import GuestFeedbackUploadDialog from "./GuestFeedbackUploadBatchDialog";
import { GuestFeedbackBatchDetailsFragment } from "./GuestFeedbackUploadBatchDialog.generated";
import { useGuestFeedbackUploadBatchesQuery } from "./GuestFeedbackUploadBatches.generated";
import { useGuestFeedbackAnalysisRunStatusQuery } from "./GuestFeedbackView.generated";
import { csvUploadFileName } from "./csvUploadFileName";

const labels: Record<GuestFeedbackUploadBatchStatus, string> = {
  PROCESSING: "Importing reviews",
  NEEDS_ATTENTION: "Some files need attention",
  WAITING_FOR_ANALYSIS: "Waiting for analysis",
  ANALYZING: "Analyzing feedback",
  COMPLETED: "Analysis complete",
  ANALYSIS_FAILED: "Analysis failed",
  NO_NEW_FEEDBACK: "No new reviews",
  CANCELLED: "Cancelled",
};

const finishedStatuses = new Set([
  GuestFeedbackUploadBatchStatus.Completed,
  GuestFeedbackUploadBatchStatus.NoNewFeedback,
  GuestFeedbackUploadBatchStatus.Cancelled,
]);

const inProgressStatuses = new Set([
  GuestFeedbackUploadBatchStatus.Processing,
  GuestFeedbackUploadBatchStatus.WaitingForAnalysis,
  GuestFeedbackUploadBatchStatus.Analyzing,
]);

export const UPLOAD_BATCHES_PAGE_SIZE = 50;
const ACTIVE_POLL_MS = 5000;
const IDLE_POLL_MS = 30000;

export default function GuestFeedbackUploadBatches(): React.ReactElement | null {
  const router = useRouter();
  const client = useApolloClient();
  const { addErrorToast } = useToast();
  const [limit, setLimit] = useState(UPLOAD_BATCHES_PAGE_SIZE);
  const [editing, setEditing] = useState<GuestFeedbackBatchDetailsFragment>();
  const observedBatches = useRef(new Set<string>());
  const notifiedRuns = useRef(new Set<string>());
  const importedFiles = useRef<Set<string> | null>(null);
  const [pollInterval, setPollInterval] = useState(ACTIVE_POLL_MS);
  const { data, previousData, error, refetch } =
    useGuestFeedbackUploadBatchesQuery({
      variables: { limit },
      pollInterval,
      fetchPolicy: "cache-and-network",
    });
  // Every run for the org, including weekly re-analyses that have no visible
  // upload batch. A failed batch is stale once any later run has started.
  const { data: runsData } = useGuestFeedbackAnalysisRunStatusQuery({
    pollInterval,
  });
  const result = data ?? previousData;
  const latestRunStartedAt = Math.max(
    0,
    ...(runsData?.GuestFeedbackAnalysisRuns ?? []).map((run) =>
      Date.parse(run.startedAt),
    ),
  );
  const isSuperseded = (batch: GuestFeedbackBatchDetailsFragment) =>
    batch.status === GuestFeedbackUploadBatchStatus.AnalysisFailed &&
    !!batch.analysisRun &&
    Date.parse(batch.analysisRun.startedAt) < latestRunStartedAt;

  useEffect(() => {
    if (!data) return;
    setPollInterval(
      data.GuestFeedbackUploadBatches.objects.some((batch) =>
        inProgressStatuses.has(batch.status),
      )
        ? ACTIVE_POLL_MS
        : IDLE_POLL_MS,
    );
  }, [data]);

  useEffect(() => {
    if (!data || error) return;
    const imported = data.GuestFeedbackUploadBatches.objects
      .flatMap((batch) => batch.files)
      .filter((file) => file.status === GuestFeedbackCsvUploadStatus.Imported);
    // Files already imported when the page loaded are in the table already.
    if (!importedFiles.current) {
      importedFiles.current = new Set(imported.map((file) => file.id));
      return;
    }
    const seen = importedFiles.current;
    const newlyImported = imported.filter((file) => !seen.has(file.id));
    if (!newlyImported.length) return;
    newlyImported.forEach((file) => seen.add(file.id));
    void client
      .refetchQueries({
        include: [GraphqlOperations.Query.OrgGuestFeedbackCsvUploads],
      })
      .catch(() =>
        addErrorToast({
          message:
            "Reviews imported, but the uploads table couldn't refresh. Reload to see them.",
          callsite: "guest_feedback_import_refresh",
        }),
      );
  }, [data, error, client, addErrorToast]);

  useEffect(() => {
    if (!data || error) return;
    let noNewFeedback = false;
    for (const batch of data.GuestFeedbackUploadBatches.objects) {
      // Only announce work seen in progress on this page, not past runs.
      if (!finishedStatuses.has(batch.status)) {
        observedBatches.current.add(batch.id);
      } else if (observedBatches.current.delete(batch.id)) {
        const runId = batch.analysisRun?.id;
        if (
          batch.status === GuestFeedbackUploadBatchStatus.Completed &&
          runId &&
          !notifiedRuns.current.has(runId)
        ) {
          notifiedRuns.current.add(runId);
          toast.success("Analysis complete", {
            duration: 10000,
            action: {
              label: "View recommendations",
              onClick: () => void router.push("/recommendations"),
            },
          });
        } else if (
          batch.status === GuestFeedbackUploadBatchStatus.NoNewFeedback
        ) {
          noNewFeedback = true;
        }
      }
    }
    if (noNewFeedback) toast.info("No new reviews to analyze");
  }, [data, error, router]);

  const batches = result?.GuestFeedbackUploadBatches.objects ?? [];
  const groups = new Map<string, GuestFeedbackBatchDetailsFragment[]>();
  for (const batch of batches) {
    if (finishedStatuses.has(batch.status) || isSuperseded(batch)) continue;
    const key = batch.analysisRun
      ? `${batch.status}:${batch.analysisRun.id}`
      : batch.status === GuestFeedbackUploadBatchStatus.NeedsAttention
        ? batch.id
        : batch.status;
    const group = groups.get(key) ?? [];
    group.push(batch);
    groups.set(key, group);
  }
  return (
    <>
      {error && (
        <p role="status" className="text-sm text-text-secondary">
          {result
            ? "Couldn't refresh upload status. Retrying…"
            : "Couldn't load upload status. Retrying…"}
        </p>
      )}
      {groups.size > 0 && (
        <div className="divide-y divide-border-primary rounded-lg border border-border-primary bg-bg-secondary">
          {Array.from(groups, ([key, group]) => {
            const batch = group[0];
            const files = group.flatMap((item) => item.files);
            const needsAttention =
              batch.status === GuestFeedbackUploadBatchStatus.NeedsAttention;
            const failed =
              needsAttention ||
              batch.status === GuestFeedbackUploadBatchStatus.AnalysisFailed;
            const waiting =
              batch.status ===
              GuestFeedbackUploadBatchStatus.WaitingForAnalysis;
            return (
              <div key={key} className="flex items-start gap-3 px-4 py-3">
                {failed ? (
                  <AlertCircle className="mt-0.5 size-4 shrink-0 text-status-error-text" />
                ) : waiting ? (
                  <Clock3 className="mt-0.5 size-4 shrink-0 text-icon-secondary" />
                ) : (
                  <Loader2 className="mt-0.5 size-4 shrink-0 animate-spin text-icon-secondary" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary">
                    {labels[batch.status]}
                  </p>
                  {batch.status ===
                    GuestFeedbackUploadBatchStatus.Analyzing && (
                    <p className="mt-0.5 text-sm text-text-secondary">
                      Results will appear in Recommendations. You can leave this
                      page.
                    </p>
                  )}
                  {waiting && (
                    <p className="mt-0.5 text-sm text-text-secondary">
                      These uploads will be included in the next analysis.
                    </p>
                  )}
                  <details
                    className="mt-1 text-sm text-text-secondary"
                    open={failed}
                  >
                    <summary className="w-fit cursor-pointer hover:text-text-primary">
                      {files.length} CSV{" "}
                      {files.length === 1 ? "upload" : "uploads"}
                    </summary>
                    <ul className="mt-2 flex flex-col gap-1">
                      {files.map((file) => (
                        <li key={file.id}>
                          <span className="break-all">
                            {csvUploadFileName(file)}
                          </span>
                          {file.error && (
                            <p role="alert" className="text-status-error-text">
                              {file.error}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                  {batch.analysisRun?.error && (
                    <p
                      role="alert"
                      className="mt-1 text-sm text-status-error-text"
                    >
                      {batch.analysisRun.error}
                    </p>
                  )}
                </div>
                {needsAttention && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(batch)}
                  >
                    Review files
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {result && result.GuestFeedbackUploadBatches.totalCount > limit && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2"
          onClick={() => setLimit((value) => value + 50)}
        >
          Load older upload statuses
        </Button>
      )}
      {editing && (
        <GuestFeedbackUploadDialog
          open
          batch={editing}
          onOpenChange={(value) => {
            if (!value) {
              setEditing(undefined);
              void refetch();
            }
          }}
        />
      )}
    </>
  );
}

gql`
  query GuestFeedbackUploadBatches($limit: Int!) {
    GuestFeedbackUploadBatches(pagination: { limit: $limit }) {
      objects {
        ...GuestFeedbackBatchDetails
      }
      totalCount
    }
  }
`;
